import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { usePublicCategories } from '@src/hooks/queries/useCategories'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import type { PublicCategory } from '@src/types'
import { colors } from '@src/theme/sharedComponents'

const Section = styled.section`
  background: ${colors.background};
  padding: 4rem 1.5rem;
  width: 100%;
  border-top: 1px solid ${colors.borderLight};
`

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

const SectionHeader = styled.div`
  margin-bottom: 2.5rem;
`

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.4rem;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 900;
  color: ${colors.textLightMode};
  letter-spacing: -0.03em;
  margin: 0 0 0.35rem;
  line-height: 1;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
`

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${colors.textLightModeMuted};
  margin: 0;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const Card = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  background: ${colors.background};
  border: 1px solid ${colors.borderLight};
  position: relative;
  overflow: hidden;
  transition: background 0.15s;

  &:hover {
    background: color-mix(in srgb, var(--color-primary) 6%, ${colors.background});
  }

  &:hover .card-arrow { color: ${colors.accent}; transform: translateX(4px); }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: -2px;
  }
`

const CardImage = styled.div<{ $src: string }>`
  width: 100%;
  aspect-ratio: 4 / 3;
  background-image: url(${p => p.$src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: color-mix(in srgb, var(--color-primary) 4%, ${colors.backgroundAlt});
`

const CardBody = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-top: 1px solid ${colors.borderLight};
`

const CardName = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${colors.textLightMode};
  letter-spacing: 0.01em;
  text-transform: uppercase;
`

const CardArrow = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLightModeMuted};
  transition: color 0.15s, transform 0.15s;
  flex-shrink: 0;
`

const CategorySection: React.FC = () => {
  const navigate = useNavigate()
  const { data } = usePublicCategories()
  const { data: siteSettings } = usePublicSettings()

  const categories = useMemo(() => {
    if (!data?.categories) return []
    return (data.categories as PublicCategory[]).filter((c): c is PublicCategory & { image_url: string } => c.image_url !== null)
  }, [data])

  if (!data || categories.length === 0) return null

  return (
    <Section>
      <Inner>
        <SectionHeader>
          {siteSettings?.category_section_eyebrow && <Eyebrow>{siteSettings.category_section_eyebrow}</Eyebrow>}
          <SectionTitle>{siteSettings?.category_section_title ?? 'Shop the Catalog'}</SectionTitle>
          {siteSettings?.category_section_subtitle && (
            <SectionSubtitle>{siteSettings.category_section_subtitle}</SectionSubtitle>
          )}
        </SectionHeader>
        <Grid>
          {categories.map((cat) => (
            <Card
              key={cat.name}
              onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
              aria-label={`Browse ${cat.name}`}
            >
              <CardImage $src={cat.image_url} />
              <CardBody>
                <CardName>{cat.name}</CardName>
                <CardArrow className="card-arrow">
                  <i className="bi bi-arrow-right" />
                </CardArrow>
              </CardBody>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Section>
  )
}

export default CategorySection
